<script setup lang="ts">
import {computed} from "vue";
import EpisodRef from "../Models/EpisodRef";
import Session from "../Models/Session";
import EpisodRefs from "../Models/EpisodRefs";
import type { SportSession } from "@/composables/useSportSessions";
import ParticipantBadges from "../../ParticipantBadges.vue";

interface Props {
    date: string,
    tabId: string,
    active?: boolean,
    planning: Session[],
    hubs: EpisodRef[],
    studios: EpisodRef[],
    refs: EpisodRefs,
    mySessions?: SportSession[]
}

const props = withDefaults(defineProps<Props>(), {active: false});

const cells = computed<{hub: EpisodRef, studio: EpisodRef}[]>(() => {
    const cells:{hub: EpisodRef, studio: EpisodRef}[] = [];
    props.hubs?.forEach((hub, hubIndex) => {
        props.studios?.forEach(studio => {
            cells.push({hub, studio, hubIndex});
        })
    })

    return cells;
});
const hourRange = Array.from({ length: 22 - 6 + 1 }, (_, i) => i + 6)


const sessionsByRoomAndTime = computed<{[key: string]: Session[]}>(() => {
    let detailledPlanning: {[key: string]: Session[]} = {};

    cells.value.forEach(({studio, hub}) => {
        hourRange.forEach(hour => {
            const key = hub.key + studio.key + hour;

            detailledPlanning[key] = props.planning.filter(session => {
                return session.hour == hour && session.hub == hub.key && session.room == studio.key;
            })
        })
    })

    return detailledPlanning;
});

const coachesMap = computed<{[key: string]: string}>(() => {
    const coaches: {[key: string]: string} = {};

    props.refs.coaches.forEach(coach => {
        coaches[coach.key] = coach.label;
    })

    return coaches;
})
const sportsMap = computed<{[key: string]: string}>(() => {
    const sports: {[key: string]: string} = {};

    props.refs.sports.forEach(sport => {
        sports[sport.key] = sport.label;
    })

    return sports;
})
const sportsMapShort = computed<{[key: string]: string}>(() => {
    const sports: {[key: string]: string} = {};

    props.refs.sports.forEach(sport => {
        const splittedLabel = sport.label.split(' ');
        sports[sport.key] = splittedLabel.length > 1 ? splittedLabel[1] : sport.label;
    })

    return sports;
})

const mySessionsMap = computed<Record<string, SportSession>>(() => {
    const map: Record<string, SportSession> = {};
    if (props.mySessions) {
        for (const ms of props.mySessions) {
            if (ms.uid) {
                map[ms.uid] = ms;
            }
        }
    }
    return map;
});

const getSessionBookings = (sessionId: number) => {
    return mySessionsMap.value['episod_' + sessionId] || null;
};
</script>

<template>
    <div :class="{'tab-pane fade': true, 'show active': props.active}" :id="props.tabId + '-tab-pane'" role="tabpanel" :aria-labelledby="props.tabId + '-tab'" tabindex="0">
        <table class="table table-bordered table-striped">
            <thead>
            <tr>
                <th rowspan="2" style="width: 50px "></th>
                <th v-for="hub in hubs" :key="hub.key" :colspan="props.studios.length">{{ hub.label }}</th>
            </tr>
            <tr>
                <th v-for="{hub, studio} in cells" :key="hub.key + studio.key">{{studio.label}}</th>
            </tr>
            </thead>
            <tbody>
                <tr v-for="hour in hourRange">
                    <th>{{ hour }}</th>
                    <td v-for="{hub, studio, hubIndex} in cells" :key="hub.key + studio.key + hour" :class="'hub-'+hubIndex">

                        <template v-for="session in sessionsByRoomAndTime[hub.key + studio.key + hour]" :key="session.id">
                            <a v-for="booking in [getSessionBookings(session.id)]" 
                               :key="'booking-' + session.id"
                               :href="(booking && !booking.isShared && booking.cancelUrl) ? booking.cancelUrl : 'https://www.episod.com/reservation/'+ session.id" 
                               target="_blank" 
                               class="session btn btn-default" 
                               :class="{ 'booked': booking }"
                               :style="'height: ' + Math.floor(session.duration/60*100)+ '%; top: '+ session.minute/60*100 + '%'">
                                
                                <div class="session-content">
                                    <span class="d-inline d-lg-none">{{ sportsMapShort[session.activity]}}</span>
                                    <span class="d-none d-lg-inline">{{ sportsMap[session.activity]}}</span>
                                    <i class="m-2">{{ coachesMap[session.coach] }}</i>
                                    <br />
                                    
                                    <ParticipantBadges 
                                        v-if="booking"
                                        :participants="booking.participants"
                                        :isShared="booking.isShared"
                                        size="sm"
                                        :showText="true"
                                    />
                                </div>
                            </a>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.hub-0 + .hub-1,
.hub-1 + .hub-2,
.hub-2 + .hub-3,
.hub-3 + .hub-4,
.hub-4 + .hub-5
{
    border-left: 2px solid rgb(150, 150, 150);
}

.session {
    display: block;
    border: 1px solid black;
    background-color: lightblue;
    text-align: center;
    padding: 5px;
    position: relative;
}

.session:hover {
    background-color: skyblue;
}

.table {
    table-layout: fixed;
}

.session-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
}
</style>
